import type { Column } from '../../core/column'
import type { AnyColumn, Table } from '../../core/table'
import type { DatabaseDriver } from '../../core/types'

type CopyColumnValue<C> =
  C extends Column<infer T, infer I, infer _G>
    ? I extends 'optional'
      ? T | null
      : T
    : never

export type CopyColumnMap<TableColumns extends Record<string, AnyColumn>> =
  Record<string, TableColumns[keyof TableColumns]>

export type CopyRow<Selected extends Record<string, AnyColumn>> = {
  [K in keyof Selected]: CopyColumnValue<Selected[K]>
}

export type CopyRowsInput<Selected extends Record<string, AnyColumn>> =
  | Iterable<CopyRow<Selected>>
  | AsyncIterable<CopyRow<Selected>>

export interface CopyFromArgs<Selected extends Record<string, AnyColumn>> {
  columns: Selected
  rows: CopyRowsInput<Selected>
}

const textEncoder = new TextEncoder()
const COPY_TEXT_FLUSH_EVERY_ROWS = 10_000
const COPY_TEXT_FLUSH_EVERY_CHARS = 2_000_000

export function buildCopyFromSql(
  table: Table<Record<string, AnyColumn>>,
  columns: readonly AnyColumn[],
): string {
  const quotedColumns = columns.map((column) => `"${column.name}"`).join(', ')
  return `COPY ${table.__sqlRef} (${quotedColumns}) FROM STDIN WITH (FORMAT csv, NULL '\\N')`
}

export function assertColumnsBelongToTable(
  table: Table<Record<string, AnyColumn>>,
  columns: readonly AnyColumn[],
): void {
  const tableColumns = new Set(Object.values(table.__columns))
  for (const column of columns) {
    if (!tableColumns.has(column)) {
      throw new Error(
        `db.copyFrom column "${column.name}" does not belong to table "${table.__name}"`,
      )
    }
  }
}

export async function* encodeCopyRows<
  Selected extends Record<string, AnyColumn>,
>(
  selectedColumns: readonly [string, AnyColumn][],
  rows: CopyRowsInput<Selected>,
): AsyncIterable<Uint8Array> {
  let bufferedRows = 0
  let bufferedCsv = ''
  for await (const row of toAsyncIterable(rows)) {
    const fields = selectedColumns.map(([key, column]) =>
      serializeCopyField(column, (row as Record<string, unknown>)[key]),
    )
    bufferedCsv += `${fields.join(',')}\n`
    bufferedRows++
    if (
      bufferedRows >= COPY_TEXT_FLUSH_EVERY_ROWS ||
      bufferedCsv.length >= COPY_TEXT_FLUSH_EVERY_CHARS
    ) {
      yield textEncoder.encode(bufferedCsv)
      bufferedCsv = ''
      bufferedRows = 0
    }
  }
  if (bufferedCsv.length > 0) {
    yield textEncoder.encode(bufferedCsv)
  }
}

function toAsyncIterable<T>(
  rows: Iterable<T> | AsyncIterable<T>,
): AsyncIterable<T> {
  if (Symbol.asyncIterator in rows) {
    return rows
  }

  return {
    // oxlint-disable-next-line typescript-eslint/require-await
    async *[Symbol.asyncIterator]() {
      yield* rows
    },
  }
}

function serializeCopyField(column: AnyColumn, value: unknown): string {
  if (value === null) return '\\N'
  if (value === undefined) {
    throw new Error(
      `db.copyFrom row is missing value for column "${column.name}"`,
    )
  }

  const encoded = column.encode(value)
  if (encoded === null) return '\\N'
  if (encoded === undefined) {
    throw new Error(
      `db.copyFrom row encoded to undefined for column "${column.name}"`,
    )
  }

  if (typeof encoded === 'string') return escapeCsv(encoded)
  if (typeof encoded === 'number') return String(encoded)
  if (typeof encoded === 'bigint') return encoded.toString()
  if (typeof encoded === 'boolean') return encoded ? 't' : 'f'
  if (encoded instanceof Date) return escapeCsv(encoded.toISOString())

  throw new Error(
    `db.copyFrom does not support CSV encoding for column "${column.name}" with value type "${typeof encoded}"`,
  )
}

function escapeCsv(value: string): string {
  if (
    value.includes(',') ||
    value.includes('"') ||
    value.includes('\n') ||
    value.includes('\r')
  ) {
    return `"${value.replaceAll('"', '""')}"`
  }
  return value
}

/**
 * Executes a COPY FROM STDIN for a postgres table.
 * Standalone function — not a method on Database — to avoid coupling db.ts to pg internals.
 * Deliberately avoids implicit transaction-state detection.
 * Semantics follow the driver instance passed by the caller, so root vs tx
 * behavior is explicit and does not depend on ambient async context.
 */
export async function copyFrom<
  TableColumns extends Record<string, AnyColumn>,
  Selected extends CopyColumnMap<TableColumns>,
>(
  driver: DatabaseDriver,
  table: Table<TableColumns> & TableColumns,
  args: CopyFromArgs<Selected>,
): Promise<void> {
  if (table.__dialect !== 'postgres') {
    throw new Error(
      `copyFrom is only supported for postgres tables, received "${table.__dialect}".`,
    )
  }

  const selectedColumns = Object.entries(args.columns)
  if (selectedColumns.length === 0) {
    throw new Error('copyFrom requires at least one selected column')
  }

  const columns = selectedColumns.map(([, column]) => column)
  assertColumnsBelongToTable(table, columns)

  await driver.copyFrom(
    buildCopyFromSql(table, columns),
    encodeCopyRows(selectedColumns, args.rows),
  )
}

export async function materializeCopyStream(
  data: AsyncIterable<Uint8Array>,
): Promise<Blob> {
  const chunks: ArrayBuffer[] = []
  for await (const chunk of data) {
    chunks.push(Uint8Array.from(chunk).buffer)
  }
  return new Blob(chunks, { type: 'text/csv' })
}
